import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    debounceTime,
    distinctUntilChanged,
    forkJoin,
    of,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import {
    DynamicProductParam,
    Product,
    ProductRepository,
} from 'src-ironside/repositories/product.repository';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { processInput } from 'src-ironside/utils/categories';
import { environment } from 'src/environments/environment.ironside';
import { DynamicDocumentService } from '../../services/dynamic-document.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-keyword-search',
    templateUrl: './keyword-search.component.html',
    styleUrls: ['./keyword-search.component.css'],
    providers: [ProductRepository],
})
export class KeywordSearchComponent implements OnInit {
    searchValue = '';
    environment = environment;

    constructor(
        private screenMediator: ScreenMediator,
        private router: Router,
        private productRepository: ProductRepository,
        private sessionStorageService: SessionStorageService,
        private dynamicDocumentService: DynamicDocumentService,
        private translate: TranslateService
    ) { }

    ngOnInit(): void {
        this.setSearchPipe();
    }

    redirectToSearchPage() {
        //this.filterDataService.filterData = { research: this.searchValue };
        //const currentFilter = this.sessionStorageService.getFilters();
        this.sessionStorageService.setFilters({ research: this.searchValue });
        this.screenMediator.toRecherche();
    }

    // global search

    private searchProduct$ = new Subject<string>();

    autocompleteSearchListProducts: any[] = [];

    setSearchPipe() {
        this.searchProduct$
            .pipe(
                debounceTime(300), // 300ms delay
                distinctUntilChanged()
            )
            .subscribe((searchProduct: string) => {
                if (searchProduct) {
                    this.autocomplete(searchProduct);
                } else {
                    this.autocompleteSearchListProducts = [];
                }
            });
    }

    processInput(term: string): string[] {
        return term
            .trim()
            .split(/[,\s]+/)
            .filter((t) => t.trim() !== '' && /^\d+$/.test(t));
    }

    autocomplete(term: string) {
        const lang =
            environment.languagesExtranet.find((lang) => lang.code === this.translate.currentLang)
                ?.locale || 'en_GB';

        const regex = /^(\d+[,\s]+)*\d+$/;

        const cleanTerm = term.trim();

        const result = regex.test(cleanTerm)
            ? processInput(cleanTerm) // nếu là số → tách mảng
            : [cleanTerm]; // nếu có chữ → giữ nguyên


        const conditions = result!?.map((el) => ({
            type: 'CONTAINS',
            value: el,
        }));

        let filters = [
            {
                fieldName: 'referenceProduct',
                operator: 'OR',
                conditions: conditions,
            },
            {
                fieldName: `shortDescription.${lang}`,
                operator: 'OR',

                conditions: conditions,
            },
            {
                fieldName: `longDescription.${lang}`,
                operator: 'OR',

                conditions: conditions,
            },
        ];

        const filters2 = [
            {
                fieldName: 'typeOfProduct',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: 'FILS',
                    },
                ],
            },
            {
                fieldName: 'statusProduct',
                conditions: [
                    {
                        type: 'EQUALS',
                        value: 'VALIDE',
                    },
                ],
            },
        ];

        const requestPayload: any = {
            collectionName: 'dynamic_product',
            filters: filters,
            sorts: { uniqueId: 'DESC' },
            page: 1,
            pageSize: 100,
            operator: 'AND',
            locales: [lang],
            filters2: filters2,
        };

        /*    this.productRepository;
        this.dynamicDocumentService
            .findProductSpecial(requestPayload, true) */

        this.dynamicDocumentService
            .findProductSpecial(requestPayload, true)
            .pipe(
                switchMap((res) => {
                    if (!res?.content?.length) return of({ original: res, statusList: [] });

                    const filters = res.content.map((el: any) => ({
                        fieldName: 'id',
                        conditions: [
                            {
                                type: 'EQUALS',
                                value: el.linkToParentProduct?.[0]?.documentId,
                            },
                        ],
                    }));

                    const body: DynamicProductParam = {
                        filters,
                        fields: ['id', 'statusProduct', 'linkToParentProduct', 'statusProductInfo'],
                        locales: [
                            environment.languagesExtranet.find(
                                (lang) => lang.code === this.translate.currentLang
                            )?.locale || 'en_GB',
                        ],
                        sorts: {
                            referenceProduct: 'DESC',
                            publishedOrder: 'DESC',
                        },
                    };

                    return forkJoin({
                        original: of(res),
                        statusList: this.dynamicDocumentService.getStatusById(body),
                    });
                })
            )
            .subscribe(({ original, statusList }) => {
                const result = original?.content
                    ?.map((el: any) => {
                        const status = statusList?.find((statusItem: any) =>
                            statusItem.linkToParentProduct?.some(
                                (link: any) => link.documentId === el.id
                            )
                        );
                        return {
                            ...el,
                            statusProductParent: status?.statusProductInfo?.valeur,
                        };
                    })
                    .filter((el: any) => el.statusProductParent === 'VALIDE');

                this.autocompleteSearchListProducts = result
                    .map((el: Product) => this.productRepository.convertProduct(el))
                    .slice(0, 5);
            });
    }

    onSearchInput(): void {
        this.searchProduct$.next(this.searchValue);
    }

    selectItem(item: any): void {
        if (!item?.id) {
            this.searchValue = item?.longDescription ?? '';
            return;
        }
        this.productRepository.findDetailById(item?.linkedProducts?.[0])?.subscribe((res) => {
            this.router.navigateByUrl(`/product/detail/${res?.referenceProduct}`);
        });
    }

    emptyList() {
        setTimeout(() => {
            this.autocompleteSearchListProducts = [];
        }, 300);
    }
}
